import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { FaShoppingCart, FaUser, FaSignOutAlt, FaHeadset, FaChevronDown } from 'react-icons/fa';
import SupportModal from './SupportModal';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogout, setShowLogout] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };

    if (showAccountDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountDropdown]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowAccountDropdown(false);
  };

  const isActive = (path) => location.pathname === path;

  const handleNavLinkClick = () => {
    // This function is no longer used in the new implementation
  };

  const handleAccountClick = () => {
    setShowAccountDropdown(!showAccountDropdown);
  };

  const handleSupportClick = () => {
    setShowAccountDropdown(false);
    setShowSupportModal(true);
  };

  const handleAccountAction = (action) => {
    setShowAccountDropdown(false);
    if (action === 'account') {
      navigate('/account');
    } else if (action === 'support') {
      setShowSupportModal(true);
    }
  };

  return (
    <>
      <nav className="navbar" style={{ boxShadow: '0 2px 12px rgba(34,34,59,0.07)' }}>
        <div className="container d-flex justify-content-between align-items-center">
          <Link to="/" className="navbar-brand">
            🍽️ Food Ordering App
          </Link>
          <ul className="navbar-nav d-flex align-items-center">
            <li>
              <Link to="/products" className={`nav-link${isActive('/products') ? ' bg-accent' : ''}`} onClick={handleNavLinkClick}>
                Menu
              </Link>
            </li>
            {user && (
              <>
                <li>
                  <Link to="/orders" className={`nav-link${isActive('/orders') ? ' bg-accent' : ''}`} onClick={handleNavLinkClick}>
                    My Orders
                  </Link>
                </li>
                <li>
                  <Link to="/cart" className={`nav-link d-flex align-items-center${isActive('/cart') ? ' bg-accent' : ''}`} onClick={handleNavLinkClick}>
                    <FaShoppingCart />
                    <span className="ms-1">Cart</span>
                    <span className="ms-1">({getCartCount()})</span>
                  </Link>
                </li>
                <li className="position-relative" ref={dropdownRef}>
                  <button
                    className={`nav-link d-flex align-items-center account-btn${isActive('/account') ? ' bg-accent' : ''}`}
                    onClick={handleAccountClick}
                    aria-label="Account menu"
                    type="button"
                  >
                    <FaUser className="me-1" />
                    <span className="fw-bold">{user ? (user.name || user.fullName || user.username || 'Account') : 'Account'}</span>
                    <FaChevronDown className="ms-1" size={12} />
                  </button>
                  
                  {showAccountDropdown && (
                    <div className="account-dropdown">
                      <button 
                        className="dropdown-item"
                        onClick={() => handleAccountAction('account')}
                      >
                        <FaUser className="me-2" />
                        My Account
                      </button>
                      <button 
                        className="dropdown-item"
                        onClick={() => handleAccountAction('support')}
                      >
                        <FaHeadset className="me-2" />
                        Support
                      </button>
                      <div className="dropdown-divider"></div>
                      <button 
                        className="dropdown-item text-danger"
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt className="me-2" />
                        Logout
                      </button>
                    </div>
                  )}
                </li>
              </>
            )}
            {!user && (
              <>
                <li>
                  <Link to="/login" className={`nav-link${isActive('/login') ? ' bg-accent' : ''}`} onClick={handleNavLinkClick}>
                    Login
                  </Link>
                </li>
                <li>
                  <Link to="/register" className={`nav-link${isActive('/register') ? ' bg-accent' : ''}`} onClick={handleNavLinkClick}>
                    Register
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
        <div className="bottom-nav d-md-none">
          <button className={`bottom-nav-link${isActive('/products') ? ' active' : ''}`} onClick={() => navigate('/products')} aria-label="Menu">
            <FaShoppingCart />
            <span>Menu</span>
          </button>
          <button className={`bottom-nav-link${isActive('/orders') ? ' active' : ''}`} onClick={() => navigate('/orders')} aria-label="My Orders">
            <FaShoppingCart />
            <span>My Orders</span>
          </button>
          <button className={`bottom-nav-link${isActive('/cart') ? ' active' : ''}`} onClick={() => navigate('/cart')} aria-label="Cart" style={{ position: 'relative' }}>
            <FaShoppingCart style={{ position: 'relative' }} />
            {getCartCount() > 0 && (
              <span className="cart-dot"></span>
            )}
            <span>Cart</span>
          </button>
          <button
            className="bottom-nav-link"
            onClick={() => setShowSupportModal(true)}
            aria-label="Support"
            type="button"
          >
            <FaHeadset />
            <span>Support</span>
          </button>
          <button
            className={`bottom-nav-link${isActive('/account') ? ' active' : ''}`}
            onClick={() => navigate('/account')}
            aria-label="Account"
            type="button"
          >
            <FaUser />
            <span>Account</span>
          </button>
        </div>
      </nav>
      
      <SupportModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
    </>
  );
};

export default Navbar; 